import merge from 'lodash/merge';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { trackerManager } from '@hero/clients';
import { GiftCollectionSizeModal } from '@hero/components/modals';
import { configs } from '@hero/configs';
import { TRACKER_EVENT } from '@hero/constants';
import { useCreatePriceLabel, useQueries } from '@hero/hooks';
import {
  ProductList,
  ProductListHeader,
  ProductListItemDetail,
  ProductSearch
} from '@hero/molecules';
import { ShoppingCart } from '@hero/organisms';
import { useSearchInfiniteProducts } from '@hero/services/product-service/hooks';

import { ProductFilters } from './ProductFilters';
import * as S from './style';

function ProductSelectionSection({
  onChangeSelectedProductList,
  selectedProducts = [],
  featureType,
  localShoppingCartEnabled = false,
  showSaveAsCollectionButton = true,
  components = {},
  onConfirmCart,
  isDraft
}) {
  const { t } = useTranslation();

  const [modalProduct, setModalProduct] = useState();
  const [product, setProduct] = useState({});
  const [isProductDetailOpen, setIsProductDetailOpen] = useState(false);
  const [isCardWidgetOpen, setIsCardWidgetOpen] = useState(false);
  const [showGiftCollectionSizeModal, setShowGiftCollectionSizeModal] = useState(false);

  const { queries, addQuery } = useQueries();

  const { min } = useCreatePriceLabel(product);

  const filtersParams = {
    categoryIds:
      typeof queries.categoryIds === 'string' ? [queries.categoryIds] : queries.categoryIds,
    collectionIds:
      typeof queries.collectionIds === 'string' ? [queries.collectionIds] : queries.collectionIds,
    searchTerm: queries.searchTerm,
    minPrice: queries.minPrice,
    maxPrice: queries.maxPrice
  };

  const paginationParams = {
    sort: queries.sort
  };

  const params = useMemo(
    () => ({
      filters: filtersParams,
      pagination: paginationParams
    }),
    [filtersParams, paginationParams]
  );

  const {
    data: productListResponse,
    isFetching,
    isFetchingNextPage,
    fetchNextPage
  } = useSearchInfiniteProducts(params);

  const productList =
    productListResponse?.pages?.reduce((result, page) => result.concat(page.docs || []), []) || [];

  const pagination =
    productListResponse?.pages?.[productListResponse?.pages.length - 1].pagination || {};

  const onSelectProduct = product => {
    setProduct(product);
    const isSelected =
      selectedProducts.findIndex(item => item.merchantProductId === product.merchantProductId) >= 0;

    if (isSelected) {
      onChangeSelectedProductList(
        selectedProducts.filter(item => item.merchantProductId !== product.merchantProductId)
      );
      const price = Number(min);

      trackerManager.emit(TRACKER_EVENT.productRemoved, {
        product: {
          name: product?.name,
          sku: product?.merchantProductId,
          // category: product?.name,
          price: Number(price).toFixed(2),
          provider: product?.brand?.code
        }
      });
    } else {
      onChangeSelectedProductList([...selectedProducts, product]);
      const price = Number(min);

      trackerManager.emit(TRACKER_EVENT.productAdded, {
        product: {
          name: product?.name,
          sku: product?.merchantProductId,
          // category: product?.name,
          price: Number(price).toFixed(2),
          provider: product?.brand?.code
        }
      });
    }

    onRequestCloseProductModal();
  };

  const onRequestCloseProductModal = () => {
    setIsProductDetailOpen(false);
    setModalProduct();
  };

  const onRequestOpenProductModal = ({ product, isSelected }) => {
    setProduct(product);
    const selectedProduct = selectedProducts.find(
      p => p.merchantProductId === product.merchantProductId
    );

    const mergedMeta = merge(product.meta, selectedProduct ? selectedProduct.meta : {});

    setModalProduct({
      product: {
        ...product,
        meta: mergedMeta
      },
      isSelected,
      productWithSkuSelection: {
        ...product,
        meta: mergedMeta,
        skus: selectedProduct?.skus
      }
    });

    setIsProductDetailOpen(true);
    const price = Number(min);

    trackerManager.emit(TRACKER_EVENT.productDetail, {
      product: {
        name: product?.name,
        sku: product?.merchantProductId,
        // category: product?.name,
        price: Number(price).toFixed(2),
        provider: product?.brand?.code
      }
    });
  };

  const onUpdateProduct = product => {
    onChangeSelectedProductList(
      selectedProducts?.map(item => {
        if (item.merchantProductId === product.merchantProductId) {
          return product;
        }
        return item;
      })
    );
  };

  const [parentRef, setParentRef] = useState(null);

  useEffect(() => {
    if (configs?.productList?.defaultFilterId && !queries.categoryIds && !queries?.collectionIds) {
      addQuery('categoryIds', configs?.productList?.defaultFilterId, true);
    }
  }, [configs?.productList?.defaultFilterId]);

  return (
    <S.ProductSelectionSectionWrapper isCardWidgetOpen={isCardWidgetOpen}>
      <S.ProductListContainer ref={setParentRef}>
        <S.ProductListBody>
          <S.ProductFilterAndSearchContainer>
            <ProductFilters parentRef={parentRef} />
            <ProductSearch
              featureType={featureType}
              selectedProducts={selectedProducts}
              updateProducts={onChangeSelectedProductList}
              isDraft={isDraft}
            />
          </S.ProductFilterAndSearchContainer>

          <ProductListHeader
            featureType={featureType}
            setIsCardWidgetOpen={setIsCardWidgetOpen}
            total={pagination?.total}
            showSelectAllProductsButton={false}
          />

          <ProductList
            data={productList}
            pagination={pagination}
            isFetching={isFetching}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            openProductModal={onRequestOpenProductModal}
            selectedProducts={selectedProducts}
            onSelectProduct={onSelectProduct}
            isDraft={isDraft}
          />
        </S.ProductListBody>

        {localShoppingCartEnabled && (
          <ShoppingCart
            products={selectedProducts}
            onClearAll={() => {
              onChangeSelectedProductList([]);
            }}
            onRemoveProduct={product => {
              onChangeSelectedProductList(
                selectedProducts.filter(sp => sp.merchantProductId !== product.merchantProductId)
              );
            }}
            floatingButton={{
              location: { x: 79, y: 24 }
            }}
            onConfirmCart={onConfirmCart}
            showSaveAsCollectionButton={showSaveAsCollectionButton}
            browseShoppingButtonEnabled={false}
            translation={{
              emptyCartAddGiftTitle: t('addYourFirstGifts'),
              emptyCartAddGiftDesc: t('addYourRecognitionFirstGiftsDesc')
            }}
            components={components}
          />
        )}
      </S.ProductListContainer>
      <GiftCollectionSizeModal
        isOpen={showGiftCollectionSizeModal}
        setIsOpen={setShowGiftCollectionSizeModal}
        primaryButtonProps={{
          children: t('addAnyway'),
          onClick: () => {
            onChangeSelectedProductList(productList);
            setShowGiftCollectionSizeModal(false);
          }
        }}
      />
      {!!modalProduct && (
        <ProductListItemDetail
          isOpen={isProductDetailOpen}
          onRequestClose={onRequestCloseProductModal}
          onOk={onSelectProduct}
          okText={t('includeThisGift')}
          product={modalProduct.product}
          isSelectedProduct={modalProduct?.isSelected}
          initialSelectedSkus={modalProduct?.productWithSkuSelection?.skus}
          featureType={featureType}
          onUpdateProduct={onUpdateProduct}
          isDraft={isDraft}
        />
      )}
    </S.ProductSelectionSectionWrapper>
  );
}

ProductSelectionSection.propTypes = {
  onChangeSelectedProductList: PropTypes.func,
  selectedProducts: PropTypes.array,
  featureType: PropTypes.string,
  localShoppingCartEnabled: PropTypes.bool,
  components: PropTypes.object,
  isDraft: PropTypes.bool,
  showSaveAsCollectionButton: PropTypes.bool,
  onConfirmCart: PropTypes.func
};

export { ProductSelectionSection };
